import { personFieldTypes } from '@actions/conference';
import { personType } from '@reducers/conference';
import { TIME_ZONES } from '@services/dateTimeResource';
import colors from '@theme/colors';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import InputMask from 'react-input-mask';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PERSON_TYPES } from '@config/authentication';
import messages from './messages';
import { FlexContainer, Col } from '@layout';
import { TextInput, ToggleArrow } from '@molecules';

import { Dropdown, Icon, InvisibleButton } from '@atoms';

const types = Object.values(PERSON_TYPES);

const personHandlers = (setPersonField) => (id) => ({
  setName: ({ target: { value } }) => setPersonField(id, personFieldTypes.NAME, value),
  types: (type) => ({
    value: type,
    label: <FormattedMessage {...messages.type[type]} />,
  }),
  setType: (value) => setPersonField(id, personFieldTypes.TYPE, value),
  setPhoneNumber: ({ target: { value } }) =>
    setPersonField(id, personFieldTypes.PHONE_NUMBER, value),
  setCountryAndConferenceNumber: (conferencePhoneNumberId, countries) => {
    const countryNumber = countries.find(({ items }) =>
      _.find(items, { value: conferencePhoneNumberId }),
    );
    setPersonField(id, personFieldTypes.COUNTRY, countryNumber.name);
    setPersonField(
      id,
      personFieldTypes.CONFERENCE_PHONE_NUMBER_ID,
      conferencePhoneNumberId,
    );
  },
  setTimezone: (value) => setPersonField(id, personFieldTypes.TIME_ZONE, value),
  setEmail: ({ target: { value } }) =>
    setPersonField(id, personFieldTypes.EMAIL, value.trim()),
});

const getConferencePhoneNumber = (
  { conferencePhoneNumberId, country },
  numbersByCountries,
) => {
  const numberWithSuchId = _.chain(numbersByCountries)
    .map('items')
    .flatten()
    .find({ value: conferencePhoneNumberId })
    .value();
  if (!conferencePhoneNumberId || !numberWithSuchId) return null;
  return numberWithSuchId.label;
};

const FieldRow = styled.div`
  border: solid 1px ${({ expanded }) => (expanded ? ` ${colors.PRIMARY};` : `#d8d8d8`)};
  transition: 300ms;
  border-radius: 5px;
  margin-bottom: 12px;
`;

const HeaderItem = styled.div`
  flex: 1;
`;

export class Person extends Component {
  state = {
    expanded: false,
  };

  toggleSummary = () => {
    this.setState(({ expanded }) => ({ expanded: !expanded }));
  };

  handleEmailSendButtonClick = () => {
    const { person, requestTranscriptCompletionEmail } = this.props;
    const verb = person.isTranscriptionCompletedEmailSent ? 're-send' : 'send';
    const prompt = window.confirm(
      `Are sure to ${verb} transcription email to ${person.name}`,
    );
    if (prompt) {
      requestTranscriptCompletionEmail(person.id);
    }
  };

  render() {
    const {
      id,
      setPersonField,
      removePerson,
      person,
      conferenceHasBeenFinished,
      disabledFields = [],
      numbersByCountries,
      readOnly,
      isCurrentUserSuperAdmin,
    } = this.props;
    const handlePerson = personHandlers(setPersonField)(id);
    const conferencePhoneNumber = getConferencePhoneNumber(person, numbersByCountries);
    const { expanded } = this.state;
    const canSendTranscriptCompletionEmail = !!(
      conferenceHasBeenFinished &&
      isCurrentUserSuperAdmin &&
      person.type === PERSON_TYPES.CLIENT &&
      person.id
    );
    const summary = (
      <FlexContainer
        style={{
          color: 'grey',
          padding: '12.5px 12.5px',
          borderBottom: expanded ? '1px solid rgba(151, 151, 151, 0.5)' : 'none',
          backgroundColor: readOnly ? '#eee' : 'transparent',
        }}
        centered
      >
        <div
          onClick={this.toggleSummary}
          style={{
            padding: '0 17px 0 0',
          }}
        >
          <ToggleArrow expanded={expanded} />
        </div>
        <HeaderItem>{person.name}</HeaderItem>
        {disabledFields.includes('type') ? null : <HeaderItem> {person.type}</HeaderItem>}
        <HeaderItem> {person.phoneNumber}</HeaderItem>
        <HeaderItem> {person.country}</HeaderItem>
        {canSendTranscriptCompletionEmail && (
          <div style={{ position: 'relative' }}>
            <InvisibleButton
              id={`send-email-to-attendee-${person.id}`}
              active
              style={{
                marginRight: '10px',
              }}
              onClick={this.handleEmailSendButtonClick}
              title={`${
                person.isTranscriptionCompletedEmailSent ? 'Re-send' : 'Send'
              } email about transcript completion to ${person.name}`}
            >
              <Icon name="mail" />
            </InvisibleButton>
          </div>
        )}
        <div onClick={this.toggleSummary}>{!readOnly && <Icon name="pencilGrey" />}</div>
      </FlexContainer>
    );

    const editForm = (
      <FlexContainer
        style={{
          padding: '12.5px 12.5px',
        }}
        gap="1rem"
        centered
      >
        <TextInput
          underlined
          id={`name-${id}`}
          label={<FormattedMessage {...messages.name} />}
          value={person.name}
          onChange={handlePerson.setName}
          disabled={readOnly}
        />
        {disabledFields.includes('type') ? null : (
          <Col width="150px">
            <Dropdown
              id={`type-${id}`}
              label={<FormattedMessage {...messages.type} />}
              value={person.type}
              options={types.map(handlePerson.types)}
              onChange={handlePerson.setType}
              disabled={readOnly}
            />
          </Col>
        )}
        <Col width="16em">
          <InputMask
            mask="+999999999999999"
            maskChar={null}
            value={person.phoneNumber}
            onChange={handlePerson.setPhoneNumber}
            disabled={readOnly}
          >
            {(inputProps) => (
              <TextInput
                underlined
                {...inputProps}
                id={`phone-number-${id}`}
                label={<FormattedMessage {...messages.phoneNumber} />}
              />
            )}
          </InputMask>
        </Col>
        <Col width="300px">
          <Dropdown
            id={`country-${id}`}
            label={<FormattedMessage {...messages.country} />}
            value={person.country}
            options={numbersByCountries}
            onChange={(value) =>
              handlePerson.setCountryAndConferenceNumber(value, numbersByCountries)
            }
            disabled={readOnly}
          />
        </Col>
        {!conferenceHasBeenFinished && (
          <Col width="150px">
            <Dropdown
              id={`timezone-${id}`}
              label={<FormattedMessage {...messages.timezone} />}
              value={person.timezone}
              options={TIME_ZONES}
              onChange={handlePerson.setTimezone}
              disabled={readOnly}
            />
          </Col>
        )}
        <Col width="350px">
          <TextInput
            underlined
            id={`email-${id}`}
            label={<FormattedMessage {...messages.email} />}
            value={person.email}
            onChange={handlePerson.setEmail}
            disabled={readOnly}
          />
        </Col>
        {!readOnly && (
          <Col>
            <InvisibleButton id={`remove-${id}`} onClick={() => removePerson(id)}>
              <Icon name="crossGrey" />
            </InvisibleButton>
          </Col>
        )}
      </FlexContainer>
    );

    return (
      <FieldRow expanded={expanded}>
        {summary}
        {expanded && editForm}
      </FieldRow>
    );
  }
}

Person.propTypes = {
  id: PropTypes.number,
  setPersonField: PropTypes.func.isRequired,
  person: personType,
};



// WEBPACK FOOTER //
// ./components/molecules/Person/Person.js